import { Box, VBox } from "./Box";
import { Button, IconButton } from "./Button";
import { FullScreenLayout } from "./FullScreenLayout";
import { PdfPreview } from "./PdfPreview";
import {
  getExtension,
  isExtensionImage,
  isExtensionJson,
  isExtensionPdf,
  isExtensionXml,
} from "@/utils/files";
import { highlightXml } from "@/utils/xml";
import { EmptyState } from "./EmptyState";
import unsupportedFilePreview from "@/assets/img/unsupported-file-preview.svg";
import { downloadFile } from "@/api";
import { Dialog } from "./Dialog";

import { useTranslation } from "@/stores/RootStoreContext";
import { LoadingIndicator } from "./LoadingIndicator";
import { ReactNode, useEffect, useState } from "react";
import { logError } from "@/utils/log";
import { isTMessage, TFunction, TMessage } from "@/i18n/tt";
import { isFailure, isOk, isPending, Result } from "@/common/common";
import { compact } from "lodash";
import { Tag } from "./Tag";
import { Alert } from "./Alert";
import { UrnReference } from "@/utils/UrnReference";
import { observer } from "mobx-react";
import { DCEvent } from "@/events/eventsDomain";

type FilePreviewDialogProps = FilePreviewProps & {
  onClose?: (() => void) | DCEvent[];
};

export type PreviewRenditionStatus = "READY" | "GENERATING" | "FAILED";

export type PreviewRendition = { status: PreviewRenditionStatus; url?: string };

export type FilePreviewProps = {
  filename?: string | TMessage;
  file: { url: string } | { data: ArrayBuffer | Blob | File };
  width?: "sm" | "md" | "lg" | "auto";
  extraContent?: ReactNode;
  conversion?: ConversionProps;
  action?: ReactNode;
  rendition?: PreviewRendition;
};

export const FilePreview = observer(function FilePreview({
  filename = "",
  file,
  width,
  extraContent,
  conversion,
  action,
  rendition,
}: FilePreviewProps) {
  const { t } = useTranslation();
  const actualFilename = toFilename(filename, t) ?? "";
  const onDownload = () => downloadFile(file, actualFilename);
  const container = (children: ReactNode) => (
    <Box
      justifyContent={"center"}
      marginX={width === "auto" ? undefined : "auto"}
      marginTop
      marginBottom
    >
      {children}
    </Box>
  );

  switch (true) {
    case isExtensionPdf(actualFilename):
      return container(
        <PdfPreview file={file} width={width} extraContent={extraContent} action={action} />,
      );
    case isExtensionImage(actualFilename) && "url" in file:
      return container(<ImagePreview filename={actualFilename} url={file.url} />);
    case isExtensionJson(actualFilename):
      return container(<JsonPreview file={file} />);
    case isExtensionXml(actualFilename):
      return container(<XmlPreview file={file} />);
    case rendition?.status === "READY" && rendition.url !== undefined:
      return container(
        <PdfPreview
          file={{ url: rendition.url }}
          width={width}
          extraContent={extraContent}
          action={action}
        />,
      );
    case rendition?.status === "GENERATING":
      return (
        <VBox grow={"golden"} justifyContent={"center"}>
          <LoadingIndicator label={{ key: "file-preview.generating-preview" }} />
        </VBox>
      );
    default:
      return (
        <VBox grow={"golden"} justifyContent={"center"}>
          <UnsupportedFile
            filename={actualFilename}
            onDownload={onDownload}
            conversion={conversion}
          />
        </VBox>
      );
  }
});

export const FilePreviewDialog = observer(function FilePreviewDialog(
  props: FilePreviewDialogProps,
) {
  const { t } = useTranslation();
  const { conversion } = props;
  const filename = toFilename(conversion?.filename ?? props.filename, t);
  function renderHeader() {
    return (
      <Box bg borderBottom padding alignItems={"center"} flex={"none"}>
        {props.onClose && (
          <IconButton
            icon={"x"}
            tooltip={{ key: "close" }}
            onClick={props.onClose}
            color={"inherit"}
          />
        )}
        {filename && (
          <>
            <div>{filename}</div>
            {isOk(conversion?.conversionResult) ? (
              <Box className={"ml-auto"} alignItems={"center"}>
                {t("file-preview.keep-question")}
                <Button
                  icon="check"
                  variant={"primary"}
                  label={t("file-preview.keep")}
                  size={"sm"}
                  loading={isPending(conversion?.keepDiscardResult)}
                  onClick={conversion?.onKeep}
                />
                <Button
                  size={"sm"}
                  variant={"secondary"}
                  icon={"arrow-counter-clockwise"}
                  label={t("file-preview.discard", { extension: getExtension(filename) })}
                  loading={isPending(conversion?.keepDiscardResult)}
                  onClick={conversion?.onBackToOriginalFile}
                />
              </Box>
            ) : (
              <Button
                className={"ml-auto"}
                icon={"download"}
                label={{ key: "download" }}
                variant={"secondary"}
                onClick={() => downloadFile(props.file, filename)}
              />
            )}
          </>
        )}
      </Box>
    );
  }

  return (
    <Dialog fullscreen onCancel={props.onClose}>
      <FullScreenLayout
        appBar={renderHeader()}
        pageHeader={
          isOk(conversion?.keepDiscardResultMessage) ? (
            <VBox padding>
              <Alert icon type="success" message={conversion?.keepDiscardResultMessage.data} />
            </VBox>
          ) : isFailure(conversion?.keepDiscardResultMessage) ? (
            <VBox padding>
              <Alert icon type="error" message={conversion?.keepDiscardResultMessage.error} />
            </VBox>
          ) : undefined
        }
        centerPanel={
          isOk(conversion?.conversionResult) ? (
            <FilePreview
              {...{
                ...props,
                conversion,
                file: { url: `/api/convert/${conversion.conversionResult.data}/get` },
                filename: `${filename}.pdf`,
              }}
            />
          ) : isOk(conversion?.keepDiscardResult) ? (
            <FilePreview
              {...{
                ...props,
                conversion,
                file: { data: conversion.keepDiscardResult.data.data },
                filename: conversion.keepDiscardResult.data.filename,
              }}
            />
          ) : (
            <FilePreview {...{ ...props, conversion }} />
          )
        }
      />
    </Dialog>
  );
});

function ImagePreview({ filename, url }: { filename: string; url: string }) {
  return <img src={url} alt={filename} className={"max-w-full"} />;
}

const UnsupportedFile = observer(function UnsupportedFile({
  filename,
  onDownload,
  conversion,
}: {
  filename: string;
  onDownload?: () => void;
  conversion?: ConversionProps;
}) {
  const { t } = useTranslation();
  const isConversionPending = isPending(conversion?.conversionResult);
  return (
    <EmptyState
      illustration={<img src={unsupportedFilePreview} alt={"Unsupported file type illustration"} />}
      description={t(
        isConversionPending ? "file-preview.converting" : "file-preview.unsupported-file-type",
        { extension: getExtension(filename) },
      )}
      actions={
        <VBox>
          {isFailure(conversion?.conversionResult) && (
            <Alert
              message={{ key: "file-preview.convert-to-pdf.error", params: { filename } }}
              type={"info"}
            />
          )}
          {isConversionPending ? (
            <VBox gap={"lg"} alignItems={"center"}>
              <div>{t("file-preview.converting-in-progress")}</div>
              <LoadingIndicator size={"lg"} label={""} />
              <div>
                <Button variant={"secondary"} label={t("cancel")} onClick={conversion.onCancel} />
              </div>
            </VBox>
          ) : (
            compact([
              conversion?.isSupportedFileType && (
                <Button
                  key={"convert"}
                  variant={"secondary"}
                  label={
                    <div>
                      {t("file-preview.convert-to-pdf")} <Tag color="info" label={t("beta")} />
                    </div>
                  }
                  icon={"arrow-clockwise"}
                  onClick={() => conversion.onConvert()}
                />
              ),
              onDownload && (
                <Button
                  key={"download"}
                  variant={"secondary"}
                  label={t("download")}
                  icon={"download"}
                  onClick={onDownload}
                />
              ),
            ])
          )}
        </VBox>
      }
    />
  );
});

export type ConversionProps = {
  context?: UrnReference;
  isSupportedFileType: boolean;
  conversionResult?: Result<string>;
  keepDiscardResult?: Result<{ data: Blob; filename: string }>;
  keepDiscardResultMessage?: Result<TMessage, TMessage>;
  onConvert: () => void;
  onCancel: () => void;
  onKeep: () => void;
  onBackToOriginalFile: () => void;
  filename: string;
};

export type TextPreviewProps = {
  file: { url: string } | { data: ArrayBuffer | Blob };
};

function useFileText(file: TextPreviewProps["file"]) {
  const [data, setData] = useState<string | undefined>(undefined);

  useEffect(() => {
    if ("data" in file) {
      if (file.data instanceof ArrayBuffer) {
        setData(new TextDecoder("utf-8").decode(file.data));
      } else if (file.data instanceof Blob) {
        file.data.text().then(setData);
      } else {
        logError(":Not supported:", { file });
      }
    }
  }, [file]);

  useEffect(() => {
    if ("url" in file) {
      fetch(file.url)
        .then((response) => response.text())
        .then(setData);
    }
  }, [file]);
  return data;
}

function JsonPreview({ file }: TextPreviewProps) {
  const data = useFileText(file);
  return data === undefined ? (
    <LoadingIndicator />
  ) : (
    <HighlightedSource html={highlightJson(data)} />
  );
}

function XmlPreview({ file }: TextPreviewProps) {
  const data = useFileText(file);
  return data === undefined ? (
    <LoadingIndicator />
  ) : (
    <HighlightedSource html={highlightXml(data)} />
  );
}

function HighlightedSource({ html }: { html: string }) {
  return (
    <div>
      <pre
        style={{
          background: "#fff",
          padding: "0.5rem",
          boxShadow: "0 0 0 1px #e5e7eb",
          borderRadius: "0.375rem",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

function toFilename(filename: string | TMessage | undefined, t: TFunction): string | undefined {
  if (filename === undefined) {
    return undefined;
  } else if (isTMessage(filename)) {
    return t(filename);
  } else {
    return filename;
  }
}

const colors = {
  string: "#a93d3d",
  number: "#b5cea8",
  boolean: "#569cd6",
  null: "#569cd6",
  key: "#1e517b",
} as const;

function highlightJson(input: string) {
  let json;
  try {
    json = JSON.parse(input);
  } catch (e) {
    logError(e, "Failed to parse JSON", { input });
  }
  if (typeof json !== "string") {
    json = JSON.stringify(json, undefined, 2);
  }
  json = json.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+s-]?\d+)?)/g,
    function (match) {
      let type: keyof typeof colors = "number";
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          type = "key";
        } else {
          type = "string";
        }
      } else if (/true|false/.test(match)) {
        type = "boolean";
      } else if (/null/.test(match)) {
        type = "null";
      }
      return `<span style="color:${colors[type]}">${match}</span>`;
    },
  );
}
